import { Divider, Card, Pagination, Button, message } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { DownloadOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import styled from 'styled-components';
import baseuri from '../../methods/baseuri';
import SearchFilter from '../../component/Filter';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { getReferrals } from '../../methods/redux/reducers/referrals';
import {
	getUserProfileById,
	getUserReferralOverview,
} from '../../methods/redux/reducers/user-profile';
import remakeQueryString from '../../methods/utils/remake-query-string';
import ReferralRecordItem from './RecordItem';

const ReferralReportOverviewStyle = styled.div`
	& > * {
		display: inline-block;
		padding: 10px;

		& .red {
			color: var(--red-color);
		}

		& .label {
			color: #aaaaaa;
		}
	}
`;

const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const ReferralReport = () => {
	const { userId } = useParams();
	const dispatch = useAppDispatch();

	const { referrals } = useAppSelector((store) => store.referrals);

	const [searchParams, setSearchParams] = useSearchParams();
	const { user, referralOverview } = useAppSelector(
		(store) => store.userProfile
	);

	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([] as any);
	const getQueryParams = () => Object.fromEntries(searchParams.entries());
	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(referrals.paginator.perPage),
			});
		}

		if (userId) {
			dispatch(getUserProfileById(userId));
			dispatch(getUserReferralOverview({ ...queryData, userId }));
		}
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			if (userId) {
				dispatch(getReferrals({ ...queryData, oldUserId: userId }));
				dispatch(getUserReferralOverview({ ...queryData, userId }));
			} else {
				dispatch(getReferrals(queryData));
			}
		}
	}, [searchParams]);

	// Transform the results data to match the headers
	const transformDownloadRequests = (requests: any[]) =>
		requests?.map((data: any) => ({
			newUserId: data?.newUserId?.username,
			newUserBonus: `${data.newUserRewardAmount} ${
				data.currencyId?.symbol ? data.currencyId?.symbol : ''
			} ${data.newUserClaimedReward ? '(Claimed)' : ''}`,
			referrer: data?.oldUserId?.username,
			referrerBonus: `${data.oldUserRewardAmount} ${
				data.currencyId?.symbol ? data.currencyId?.symbol : ''
			} ${data.oldUserClaimedReward ? '(Claimed)' : ''}`,
			createdAt: moment(data.createdAt).format('lll'),
		}));

	// Define headers with simple keys
	const headers = [
		{ label: 'New User', key: 'newUserId' },
		{ label: 'New User Bonus', key: 'newUserBonus' },
		{ label: 'Referrer', key: 'referrer' },
		{ label: 'Referrer Bonus', key: 'referrerBonus' },
		{ label: 'Created At', key: 'createdAt' },
	];

	const handleDownload = () => {
		setLoading(true);
		const params1 = getQueryParams();

		setResults([]);
		baseuri
			.get('/admin/referrals' || '', {
				params: {
					...params1,
					perPage: referrals.paginator.perPage || 0,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawData = res.data.data.itemsList;
				const transformedData = transformDownloadRequests(rawData);
				setResults(transformedData);
				setLoading(false);
			})
			.catch(() => {
				message.error('could not download CSV');
				setLoading(false);
			});
	};

	useEffect(() => {
		if (results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results]);

	return (
		<div>
			<Card>
				<h1>
					Referral Report{' '}
					{userId && (
						<span>
							for{' '}
							<Link to={`/user-profile/${user.profile._id}`}>
								@{user.profile?.username}
							</Link>
						</span>
					)}
				</h1>

				<Divider />

				<SearchFilter
					title="Find Referral Records"
					searchFilters={[
						{
							label: 'Start Date',
							name: 'startDate',
							htmlElement: 'datepicker',
						},
						{
							label: 'End Date',
							name: 'endDate',
							htmlElement: 'datepicker',
						},
					]}
					retrieveQueryString={updateQueryString}
				/>

				<br />

				{userId && (
					<>
						<ReferralReportOverviewStyle>
							<div>
								<p>
									<strong>{referralOverview.numOfUniqueDeviceIds}</strong>
								</p>
								<p className="label">Unique Referrals</p>
							</div>
							<div>
								<p
									className={
										Number(referralOverview.totalReferrals) -
											Number(referralOverview.numOfUniqueDeviceIds) >
										0
											? 'red'
											: ''
									}
								>
									<strong>
										{Number(referralOverview.totalReferrals) -
											Number(referralOverview.numOfUniqueDeviceIds)}
									</strong>
								</p>
								<p className="label">Fake Referrals</p>
							</div>
							<div>
								<p>
									<strong>{referralOverview.totalReferrals}</strong>
								</p>
								<p className="label">Total Referrals</p>
							</div>
						</ReferralReportOverviewStyle>
						<Divider />
					</>
				)}

				{!userId && (
					<>
						<p>
							Total of <strong>{referrals.paginator.itemCount} items</strong>{' '}
							found
						</p>

						<Divider />
					</>
				)}

				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					total={referrals.paginator.itemCount}
					current={referrals.paginator.currentPage}
					pageSize={referrals.paginator.perPage}
				/>

				<br />

				{referrals && referrals.itemsList?.length > 0 && (
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<DownloadButton
							danger
							loading={loading}
							icon={<DownloadOutlined />}
							onClick={() => handleDownload()}
						>
							{loading ? 'Loading csv...' : 'Download CSV'}
						</DownloadButton>
						<CSVLink
							data={results}
							headers={headers}
							className="download"
							filename="ReferralReport.csv"
							ref={csvLinkRef}
						/>
					</div>
				)}

				<br />

				{referrals.itemsList.map((item: any) => (
					<ReferralRecordItem key={item._id} record={item} />
				))}

				<br />

				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					total={referrals.paginator.itemCount}
					current={referrals.paginator.currentPage}
					pageSize={referrals.paginator.perPage}
					showSizeChanger
				/>
			</Card>
		</div>
	);
};

export default ReferralReport;
